import { FileItem } from './types';
import { entriesForAction } from './selectionModel';

/**
 * Which entries a share action should act on, given what was right-clicked.
 *
 * Two rules, and both exist because getting either wrong is invisible until a
 * user notices the wrong thing happened:
 *
 * 1. **Expand to the selection** when the clicked entry is part of one —
 *    exactly what drag-and-drop bulk move already does. Sharing used to take
 *    only the clicked entry, so multi-selecting five files and choosing Share
 *    silently shared one.
 * 2. **Drop entries with no `object_id`.** A share is a row pointing at a row,
 *    so an object still served by the legacy S3 arm has nothing to point at.
 *    Dropping is better than sending: the API would refuse the whole call with
 *    `SHARE_NO_OBJECTS` if *every* id were unregistered, which would turn a
 *    mostly-valid selection into a total failure.
 *
 * Returning `[]` is meaningful — nothing in the selection is shareable — and
 * the dialog stays closed rather than opening on an empty grant.
 */
export function shareTargets(
  clicked: FileItem,
  // ReadonlySet of ENTRY IDS, matching what `useFileSelection` exposes — this
  // only ever reads. Membership is tested through `entryId`, never through the
  // display path: an entry that shares a path with another row must not drag
  // that other row into the grant (STD-029 R-2 + R-13).
  selectedIds: ReadonlySet<string>,
  visibleFiles: readonly FileItem[]
): FileItem[] {
  // Rule 1 is `entriesForAction` — the same definition drag-and-drop uses, so
  // "exactly what drag-and-drop already does" is now shared code rather than a
  // claim two copies could drift out of. Rule 2 is the filter below.
  return entriesForAction(clicked, selectedIds, visibleFiles).filter((f) => !!f.object_id);
}

/**
 * The i18n key for the share context-menu item, given what it will actually act on.
 *
 * The menu is where the user forms their expectation, so it has to describe the
 * *targets*, not the entry under the cursor. Deriving the label from the clicked
 * item alone meant right-clicking a file inside a mixed selection offered
 * "Share file..." and then shared four files and a folder — the dialog corrected
 * it a moment later, which is exactly one moment too late.
 */
export type ShareMenuLabelKey =
  | 'objectShares.menuItemMany'
  | 'objectShares.menuItemFolder'
  | 'objectShares.menuItemFile';

// The literal union (rather than `string`) is what lets `t()` type-check the
// result against its generated key paths — a typo here becomes a compile error
// instead of a raw key rendered into the menu.
export function shareMenuLabelKey(
  targetCount: number,
  clickedIsFolder: boolean
): ShareMenuLabelKey {
  if (targetCount > 1) return 'objectShares.menuItemMany';
  return clickedIsFolder ? 'objectShares.menuItemFolder' : 'objectShares.menuItemFile';
}
